import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { editorMessages } from '../messages/editorMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CutMenuProps {
  onNewCut: () => void;
  onCopyCut: () => void;
  isBusy: boolean;
}

/**
 * The cut menu (design b1: "Cut 2 of 2"): the cuts of the song, "New cut" (the AI makes a new first
 * cut, no credits) and "Copy this cut". The cuts are the versions of the cover editor strip.
 */
const CutMenuComponent: React.FC<CutMenuProps> = ({ onNewCut, onCopyCut, isBusy }) => {
  const intl = useIntl();
  const { data, cutUuid, openCut, readOnly } = useMusicVideoEditor();
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const cuts = useMemo(() => [...data.cuts].sort((a, b) => a.number - b.number), [data.cuts]);
  const current = cuts.find((cut) => cut.uuid === cutUuid);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (
        event instanceof KeyboardEvent
          ? event.key === 'Escape'
          : wrapRef.current?.contains(event.target as Node) !== true
      ) {
        setIsOpen(false);
      }
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', close);
    };
  }, [isOpen]);

  const run = (action: () => void) => {
    setIsOpen(false);
    action();
  };

  return (
    <div ref={wrapRef} className={styles.cutMenuWrap}>
      <button
        type="button"
        className={styles.cutMenuButton}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((value) => !value)}
      >
        {intl.formatMessage(editorMessages.cutMenu, { number: current?.number ?? 1, count: Math.max(1, cuts.length) })}
        <span aria-hidden>▾</span>
      </button>
      {isOpen && (
        <div className={styles.menu} role="menu">
          {cuts.map((cut) => (
            <button
              key={cut.uuid}
              type="button"
              role="menuitemradio"
              aria-checked={cut.uuid === cutUuid}
              className={clsx(styles.menuItem, cut.uuid === cutUuid && styles.menuItemOn)}
              onClick={() => run(() => openCut(cut.uuid))}
            >
              {intl.formatMessage(editorMessages.cutItem, { number: cut.number })}
              {cut.directorState === 'working' ? (
                <small>{intl.formatMessage(editorMessages.cutItemWorking)}</small>
              ) : (
                cut.madeAssetUuid !== null && <small>{intl.formatMessage(editorMessages.cutItemMade)}</small>
              )}
            </button>
          ))}
          <div className={styles.separator} />
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            disabled={readOnly || isBusy}
            title={intl.formatMessage(editorMessages.cutNewHint)}
            onClick={() => run(onNewCut)}
          >
            {intl.formatMessage(editorMessages.cutNew)}
          </button>
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            disabled={readOnly || isBusy}
            onClick={() => run(onCopyCut)}
          >
            {intl.formatMessage(editorMessages.cutCopy)}
          </button>
        </div>
      )}
    </div>
  );
};

export const CutMenu = TypedMemo(CutMenuComponent);
